import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { Shield, KeyRound, Check, MapPin, BellRing, X, Plus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

function ServiceAreaCard() {
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState({
    home_zip: "",
    radius_miles: 25,
    allowed_counties: "",
    allowed_zips: "",
    blocked_zips: "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/service-area");
        setForm({
          home_zip: data.home_zip || "",
          radius_miles: data.radius_miles ?? 25,
          allowed_counties: (data.allowed_counties || []).join("\n"),
          allowed_zips: (data.allowed_zips || []).join(", "),
          blocked_zips: (data.blocked_zips || []).join(", "),
        });
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaved(false);
    if (!/^\d{5}$/.test(form.home_zip)) {
      toast.error("Home ZIP must be 5 digits");
      return;
    }
    setSaving(true);
    try {
      const toList = (s) => s.split(/[,\n]/).map((x) => x.trim()).filter(Boolean);
      const toLines = (s) => s.split(/\n/).map((x) => x.trim()).filter(Boolean);
      await api.patch("/settings/service-area", {
        home_zip: form.home_zip.trim(),
        radius_miles: Number(form.radius_miles) || 0,
        allowed_counties: toLines(form.allowed_counties),
        allowed_zips: toList(form.allowed_zips),
        blocked_zips: toList(form.blocked_zips),
      });
      toast.success("Service area updated");
      setSaved(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <Card className="border-slate-200" data-testid="service-area-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <MapPin className="w-4 h-4 text-blue-700" />
          Service area
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="home-zip">Home ZIP</Label>
              <Input
                id="home-zip"
                inputMode="numeric"
                maxLength={5}
                value={form.home_zip}
                onChange={(e) => setForm((f) => ({ ...f, home_zip: e.target.value.replace(/\D/g, "").slice(0, 5) }))}
                data-testid="sa-home-zip"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="radius">Radius (miles)</Label>
              <Input
                id="radius"
                type="number"
                min={0}
                max={500}
                value={form.radius_miles}
                onChange={(e) => setForm((f) => ({ ...f, radius_miles: e.target.value }))}
                data-testid="sa-radius"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="counties">Always allow counties (one per line)</Label>
            <Textarea
              id="counties"
              value={form.allowed_counties}
              onChange={(e) => setForm((f) => ({ ...f, allowed_counties: e.target.value }))}
              placeholder="St. Mary's, MD&#10;Charles, MD&#10;Calvert, MD"
              className="min-h-[90px] font-mono text-sm"
              data-testid="sa-counties"
            />
            <p className="text-xs text-slate-500">One county per line. Format: <code>County Name, ST</code>. Included even if outside radius.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="allow-zips">Always allow ZIPs</Label>
              <Input
                id="allow-zips"
                value={form.allowed_zips}
                onChange={(e) => setForm((f) => ({ ...f, allowed_zips: e.target.value }))}
                placeholder="20601, 20744"
                data-testid="sa-allow-zips"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="block-zips">Always block ZIPs</Label>
              <Input
                id="block-zips"
                value={form.blocked_zips}
                onChange={(e) => setForm((f) => ({ ...f, blocked_zips: e.target.value }))}
                placeholder="20000"
                data-testid="sa-block-zips"
              />
            </div>
          </div>

          <div className="pt-2 flex items-center justify-between">
            {saved && (
              <span className="text-sm text-emerald-700 flex items-center gap-1" data-testid="sa-saved">
                <Check className="w-4 h-4" /> Saved
              </span>
            )}
            <div className="ml-auto">
              <Button type="submit" disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="sa-save-btn">
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function AlertNumbersCard() {
  const [numbers, setNumbers] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/alert-numbers")
      .then(({ data }) => setNumbers(data.numbers || []))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const saveList = async (list) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/settings/alert-numbers", { numbers: list });
      setNumbers(data.numbers);
      toast.success("Alert numbers updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const add = () => {
    if (!draft.trim()) return;
    saveList([...(numbers || []), draft.trim()]);
    setDraft("");
  };

  if (numbers === null) return null;

  return (
    <Card className="border-slate-200" data-testid="alert-numbers-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <BellRing className="w-4 h-4 text-blue-700" />
          Alert phone numbers
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <p className="text-xs text-slate-500">
          These numbers get a text when a booking request has waited 24 hours unapproved. Standard Twilio SMS rates apply per number.
        </p>
        {numbers.length === 0 && (
          <p className="text-sm text-amber-700" data-testid="alert-numbers-empty">No numbers yet — 24-hour SMS nudges are off until you add one.</p>
        )}
        <div className="space-y-2">
          {numbers.map((n) => (
            <div key={n} className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid={`alert-number-${n}`}>
              <span className="text-sm font-mono text-slate-800">{n}</span>
              <Button size="sm" variant="ghost" className="h-7 text-red-600" disabled={busy}
                onClick={() => saveList(numbers.filter((x) => x !== n))} data-testid={`alert-number-remove-${n}`}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="e.g. 3015551234"
            onKeyDown={(e) => e.key === "Enter" && add()} data-testid="alert-number-input" />
          <Button onClick={add} disabled={busy || !draft.trim()} className="bg-blue-700 hover:bg-blue-800 gap-1" data-testid="alert-number-add">
            <Plus className="w-4 h-4" /> Add
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSuccess(false);
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("New password and confirmation do not match");
      return;
    }
    if (next === current) {
      toast.error("New password must be different from current");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated");
      setCurrent("");
      setNext("");
      setConfirm("");
      setSuccess(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Manage your account and preferences.</p>
      </div>

      <Card className="border-slate-200" data-testid="account-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Shield className="w-4 h-4 text-blue-700" />
            Account
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 pt-0">
          <div className="text-xs uppercase tracking-wider text-slate-500">Signed in as</div>
          <div className="text-sm text-slate-900 font-medium" data-testid="account-email">{user?.email}</div>
          <div className="text-xs text-slate-500 mt-1">
            Role: <span className="text-slate-700 font-medium">{user?.role || "user"}</span>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="change-password-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-blue-700" />
            Change password
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Current password</Label>
              <Input
                id="current-password"
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
                required
                data-testid="current-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">New password</Label>
              <Input
                id="new-password"
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
                data-testid="new-password"
              />
              <p className="text-xs text-slate-500">Minimum 8 characters.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm new password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
                data-testid="confirm-password"
              />
            </div>
            <div className="pt-2 flex items-center justify-between">
              {success && (
                <span className="text-sm text-emerald-700 flex items-center gap-1" data-testid="password-success">
                  <Check className="w-4 h-4" /> Password updated
                </span>
              )}
              <div className="ml-auto">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-blue-700 hover:bg-blue-800"
                  data-testid="update-password-btn"
                >
                  {submitting ? "Updating…" : "Update password"}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {user?.role === "admin" && <AlertNumbersCard />}
      {user?.role === "admin" && <ServiceAreaCard />}
    </div>
  );
}
